/**
 * Wishlist Manager
 * LocalStorage-based wishlist for Shopify.
 * Exposes window.wishlist for use across all sections/pages.
 */
class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.items = this.load();
    this.init();
  }

  init() {
    this.updateUI();
    this.attachEventListeners();
    this.injectStyles();
  }

  /* ── Persistence ── */

  load() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('[Wishlist] Error loading:', e);
      return [];
    }
  }

  save() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      this.updateUI();
    } catch (e) {
      console.error('[Wishlist] Error saving:', e);
    }
  }

  /* ── Core API ── */

  add(productId, productData) {
    if (!this.has(productId)) {
      this.items.push({
        id: String(productId),
        handle: productData.handle || '',
        title: productData.title || '',
        price: productData.price || '',
        comparePrice: productData.comparePrice || '',
        image: productData.image || '',
        url: productData.url || '',
        addedAt: Date.now()
      });
      this.save();
      return true;
    }
    return false;
  }

  remove(productId) {
    this.items = this.items.filter(item => item.id !== String(productId));
    this.save();
  }

  has(productId) {
    return this.items.some(item => item.id === String(productId));
  }

  toggle(productId, productData) {
    if (this.has(productId)) {
      this.remove(productId);
      return false;
    } else {
      this.add(productId, productData);
      return true;
    }
  }

  getAll() {
    return this.items;
  }

  count() {
    return this.items.length;
  }

  clear() {
    this.items = [];
    this.save();
  }

  /* ── UI sync ── */

  updateUI() {
    // Badge counts — any element with [data-wishlist-count]
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = this.count();
      el.style.display = this.count() > 0 ? 'flex' : 'none';
    });

    // Wishlist toggle buttons — any element with [data-wishlist-button]
    document.querySelectorAll('[data-wishlist-button]').forEach(btn => {
      const productId = btn.dataset.productId;
      const inList = this.has(productId);

      btn.classList.toggle('active', inList);
      btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');

      // Fill the heart SVG path when active
      const path = btn.querySelector('svg path');
      if (path) {
        path.setAttribute('fill', inList ? 'currentColor' : 'none');
        path.setAttribute('stroke', 'currentColor');
      }
    });
  }

  /* ── Event delegation ── */

  attachEventListeners() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-button]');
      if (!btn) return;

      e.preventDefault();

      const productId = btn.dataset.productId;
      if (!productId) return;

      const productData = {
        handle:       btn.dataset.productHandle || '',
        title:        btn.dataset.productTitle  || document.querySelector('.product__title h1')?.textContent.trim() || '',
        price:        btn.dataset.productPrice  || document.querySelector('.price-item--regular')?.textContent.trim() || '',
        comparePrice: btn.dataset.productComparePrice || '',
        image:        btn.dataset.productImage  || document.querySelector('.product__media img')?.src || '',
        url:          btn.dataset.productUrl    || window.location.pathname
      };

      const added = this.toggle(productId, productData);
      this.showNotification(added ? 'Added to wishlist' : 'Removed from wishlist');
    });
  }

  /* ── Toast notification ── */

  showNotification(message) {
    // Remove any existing toast
    document.querySelector('.wl-toast')?.remove();

    const toast = document.createElement('div');
    toast.className = 'wl-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;

    document.body.appendChild(toast);

    // Trigger enter animation on next frame
    requestAnimationFrame(() => toast.classList.add('wl-toast--visible'));

    setTimeout(() => {
      toast.classList.remove('wl-toast--visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    }, 2200);
  }

  /* ── Inject toast + animation styles ── */

  injectStyles() {
    if (document.getElementById('wl-manager-styles')) return;

    const style = document.createElement('style');
    style.id = 'wl-manager-styles';
    style.textContent = `
      .wl-toast {
        position: fixed;
        bottom: 32px;
        right: 32px;
        background: #003E8B;
        color: #ffffff;
        padding: 14px 24px;
        border-radius: 8px;
        font-family: 'Poppins', sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.5;
        z-index: 99999;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        transform: translateX(calc(100% + 40px));
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
        pointer-events: none;
      }

      .wl-toast--visible {
        transform: translateX(0);
        opacity: 1;
      }

      @media (max-width: 480px) {
        .wl-toast {
          bottom: 20px;
          right: 16px;
          left: 16px;
          text-align: center;
          transform: translateY(calc(100% + 24px));
        }
        .wl-toast--visible {
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

/* ── Bootstrap ── */
if (!window.wishlist) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.wishlist) window.wishlist = new WishlistManager();
    });
  } else {
    window.wishlist = new WishlistManager();
  }
}
